import { createContext, useContext, useEffect, useState } from 'react'
import { useAccount, useChainId, useConnect, useDisconnect, useWalletClient } from 'wagmi'
import { ethers } from 'ethers'
import { CONTRACT_ABI, ESCROW_ABI, getContractAddress, getEscrowAddress } from '../config/contract'
import { walletClientToSigner } from '../utils/ethersAdapters'

const Web3Context = createContext()

export function Web3Provider({ children }) {
  const { address, isConnected } = useAccount()
  const chainId = useChainId()
  const { connect, connectors } = useConnect()
  const { disconnect } = useDisconnect()
  const { data: walletClient } = useWalletClient()
  const [contract, setContract] = useState(null)
  const [escrowContract, setEscrowContract] = useState(null)
  const [provider, setProvider] = useState(null)
  const [contractAddress, setContractAddress] = useState('')
  const [escrowAddress, setEscrowAddress] = useState('')
  // true while walletClient is resolving — prevents "contract not ready" false positives
  const [contractLoading, setContractLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function boot() {
      if (!isConnected || !address || !walletClient) {
        if (!cancelled) {
          setContract(null)
          setEscrowContract(null)
          setProvider(null)
          setContractAddress('')
          setEscrowAddress('')
          setContractLoading(false)
        }
        return
      }

      if (!cancelled) setContractLoading(true)

      const addr = getContractAddress(chainId)
      const escrowAddr = getEscrowAddress(chainId)

      const { signer, provider: ethersProvider } = await walletClientToSigner(walletClient)
      if (cancelled) return

      setProvider(ethersProvider)
      setContractAddress(addr || '')
      setContract(addr ? new ethers.Contract(addr, CONTRACT_ABI, signer) : null)
      setEscrowAddress(escrowAddr || '')
      setEscrowContract(escrowAddr ? new ethers.Contract(escrowAddr, ESCROW_ABI, signer) : null)
      setContractLoading(false)
    }

    boot().catch((err) => {
      console.error('[Web3] boot failed:', err)
      if (!cancelled) {
        setContract(null)
        setEscrowContract(null)
        setProvider(null)
        setContractAddress('')
        setEscrowAddress('')
        setContractLoading(false)
      }
    })

    return () => {
      cancelled = true
    }
  }, [isConnected, address, walletClient, chainId])

  const connectWallet = async () => {
    // RainbowKit handles this via ConnectButton
    if (connectors.length > 0) {
      connect({ connector: connectors[0] })
    }
  }

  return (
    <Web3Context.Provider value={{
      account: address,
      chainId,
      contract,
      escrowContract,
      provider,
      contractAddress,
      escrowAddress,
      isConnected,
      contractLoading,
      connect: connectWallet,
      disconnect,
    }}>
      {children}
    </Web3Context.Provider>
  )
}

export const useWeb3 = () => useContext(Web3Context)